import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import { SubmitFilterDataType } from "@saleor/changeOrderDomestic/components/OrderFilterSection/constant";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import {
  useCustomerOrdersFromSapQuery,
  useDownloadPdfOrderMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import OrderListFilter from "@saleor/inquirySearchOrder/components/OrderListFilter";
import OrderListTable from "@saleor/inquirySearchOrder/components/OrderListTable";
import {
  ExporOrderListSortField,
  exporOrderListUrl,
  ExporOrderListUrlQueryParams
} from "@saleor/inquirySearchOrder/urls";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import {
  asSortParams,
  getSortParams,
  getSortUrlVariables
} from "@saleor/utils/sort";
import { parse as parseQs } from "qs";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface OrdersViewProps {
  params: ExporOrderListUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  _ => ({
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    flex: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }
  }),
  { name: "OrdersView" }
);

export enum SearchTypeEnum {
  SOLDTO = "SoldTo",
  PI = "Pi"
}

function SectionMessage(props) {
  const classes = useStyles({});
  return (
    <Box className={classes.flex}>
      <img src={props.icon} alt="info" />
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
          color={props.color}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

const OrdersView: React.FC<OrdersViewProps> = () => {
  const intl = useIntl();
  const [
    { fullScreen, filterOrder: filterAppState },
    dispatchAppState
  ] = useAppState();
  const [filter, setFilter] = useState<SubmitFilterDataType>({});
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const qs = parseQs(location.search.substr(1));
  const params = asSortParams(
    qs,
    ExporOrderListSortField,
    ExporOrderListSortField.soNo
  );
  const classes = useStyles({ fullScreen });
  const navigate = useNavigator();

  const { updateListSettings, settings } = useListSettings(
    ListViews.CUSTOMER_ORDER
  );

  const [isOpenFilterSection, setIsOpenFilterSection] = useState<boolean>(true);

  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.CUSTOMER_ORDER.rowNumber
    );
    // filter store
    if (filterAppState) {
      setFilter(filterAppState);
      setIsSearched(true);
      setTimeout(() => {
        dispatchAppState({
          type: "setFilterOrder",
          payload: {
            filterOrder: null
          }
        });
      }, 1000);
    }
  }, []);

  const filterSAP = useMemo(
    () => ({
      bu: filter.bu || undefined,
      company:
        filter.company && filter.company !== "ALL" ? filter.company : undefined,
      contractNo: filter.contractNo || undefined,
      createDateFrom: filter.createDate?.gte || undefined,
      createDateTo: filter.createDate?.lte || undefined,
      dpNo: filter.dpNo || undefined,
      purchaseOrderNo: filter.purchaseOrderNo || undefined,
      invoiceNo: filter.invoiceNo || undefined,
      materialCode: filter.materialCodeName || undefined,
      soNo: filter.soNo || undefined,
      soldTo: filter.soldTo || undefined,
      updateDateFrom: filter.lastUpdate?.gte || undefined,
      updateDateTo: filter.lastUpdate?.lte || undefined
    }),
    [filter]
  );

  const filterSAPArrValid = Object.values(filterSAP).filter(item => item);

  const { data, loading } = useCustomerOrdersFromSapQuery({
    skip: !isSearched || !filterSAPArrValid.length,
    displayLoader: true,
    variables: {
      input: { ...filterSAP }
    }
  });

  const handleSortLocal = (list, params) => {
    const listSort = list.map(item => ({ ...item, writable: true }));
    switch (params.sort) {
      case "soNo":
      default:
        if (params.asc) {
          listSort.sort(
            (itemA, itemB) => Number(itemA.sdDoc) - Number(itemB.sdDoc)
          );
        } else {
          listSort.sort(
            (itemA, itemB) => Number(itemB.sdDoc) - Number(itemA.sdDoc)
          );
        }
        break;
      case "poNo":
        if (params.asc) {
          listSort.sort((itemA, itemB) => itemA.poNo.localeCompare(itemB.poNo));
        } else {
          listSort.sort((itemA, itemB) => itemB.poNo.localeCompare(itemA.poNo));
        }
        break;
      case "contractNo":
        if (params.asc) {
          listSort.sort(
            (itemA, itemB) =>
              Number(itemA.contractPi) - Number(itemB.contractPi)
          );
        } else {
          listSort.sort(
            (itemA, itemB) =>
              Number(itemB.contractPi) - Number(itemA.contractPi)
          );
        }
        break;
      case "createDate":
        if (params.asc) {
          listSort.sort(
            (itemA, itemB) =>
              +new Date(
                itemA.createDate
                  .split("/")
                  .reverse()
                  .join("/")
              ) -
              +new Date(
                itemB.createDate
                  .split("/")
                  .reverse()
                  .join("/")
              )
          );
        } else {
          listSort.sort(
            (itemA, itemB) =>
              +new Date(
                itemB.createDate
                  .split("/")
                  .reverse()
                  .join("/")
              ) -
              +new Date(
                itemA.createDate
                  .split("/")
                  .reverse()
                  .join("/")
              )
          );
        }
        break;
    }
    return listSort;
  };

  const listOrder =
    useMemo(() => {
      let listSort = data?.customerOrdersFromSap || [];
      listSort = handleSortLocal(listSort, params);
      return listSort;
    }, [data?.customerOrdersFromSap, params]) || [];

  const {
    currentData,
    next,
    prev,
    toFirst,
    toLast,
    disableNext,
    disablePrev
  } = usePaginationLocal(listOrder, settings.rowNumber);

  const resetPaginationState = () => {
    toFirst();
  };

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };

  const handleSort = field => {
    resetPaginationState();
    navigate(
      exporOrderListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber, filter]);

  const handleChangeFilter = (filterObj: SubmitFilterDataType) => {
    setIsSearched(true);
    setFilter({ ...filterObj });
  };

  function downloadPDF(pdf) {
    const linkSource = `data:application/pdf;base64,${pdf.exportedFileBase64}`;
    const downloadLink = document.createElement("a");
    const fileName = pdf.fileName;
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }

  const [onDownloadPdfOrder] = useDownloadPdfOrderMutation({
    onCompleted(data) {
      downloadPDF(data.downloadPdfOrder);
    }
  });

  const handleDownloadPdf = (orderId, soNo) => {
    onDownloadPdfOrder({
      variables: {
        orderId: orderId ? orderId : undefined,
        soNo: soNo ? soNo : undefined,
        sortType: getSortParams(params).asc ? "ASC" : "DESC"
      }
    });
  };

  const message = useMemo(() => {
    if (loading) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (!isSearched) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (isSearched && !data?.customerOrdersFromSap?.length) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    return {
      icon: CheckCircle,
      message: intl.formatMessage({
        defaultMessage: "พบรายการที่เกี่ยวข้อง"
      }),
      color: "#28A745"
    };
  }, [data?.customerOrdersFromSap, isSearched, loading]);

  const onClearForm = () => {
    setIsSearched(false);
  };

  const onToggleFilteSection = () => {
    setIsOpenFilterSection(!isOpenFilterSection);
  };

  useEffect(() => {
    if (listOrder.length) {
      setIsOpenFilterSection(false);
    }
  }, [listOrder.length]);

  return (
    <>
      <Container>
        <PageHeader
          className={classes.pageHeader}
          title={intl.formatMessage({
            defaultMessage: "ค้นหาใบสั่งซื้อ (Search Order)",
            description: "label"
          })}
        ></PageHeader>
        <OrderListFilter
          onToggleFilter={onToggleFilteSection}
          isOpen={isOpenFilterSection}
          message={<SectionMessage {...message} />}
          onClearForm={onClearForm}
          handleChangeFilter={handleChangeFilter}
          listOrder={listOrder}
          loading={loading}
        />
        {listOrder?.length ? (
          <OrderListTable
            pageInfo={{
              hasNextPage: !disableNext(),
              hasPreviousPage: !disablePrev()
            }}
            data={currentData()}
            onDownloadPdfOrder={handleDownloadPdf}
            settings={settings}
            onUpdateListSettings={updateListSettings}
            totalCount={listOrder.length}
            latestPageItemNumber={listOrder.length}
            onPreviousFirstPage={handlePreviousFirstPage}
            onNextLastPage={toLast}
            disabled={loading}
            onNextPage={next}
            onPreviousPage={prev}
            onRowClick={null}
            sort={getSortParams(params)}
            onSort={handleSort}
          />
        ) : (
          <></>
        )}
      </Container>
    </>
  );
};

export default OrdersView;
